<template>
  <div
    class="fixed left-0 top-0 z-50 flex h-full w-screen items-center justify-center backdrop-blur-[1px] backdrop-brightness-50"
  >
    <div
      class="flex h-full w-full flex-col bg-white p-6 transition-all duration-300 ease-in-out dark:bg-dark-1"
      :class="[
        {
          'md:h-[95vh] max-w-4xl-custom md:rounded-xl 2xl:h-[90vh]':
            !isRadiatorForm,
        },
        { 'rounded-xl': isRadiatorForm },
      ]"
    >
      <header
        id="modal-header"
        class="relative shrink-0 pb-2 text-center dark:border-dark-2 keyboard-open:hidden"
      >
        <div v-if="isDialogMinimal">
          <div class="text-3xl font-bold">
            {{ formHeading }}
          </div>

          <div v-if="deviceFormHeading" class="text-xl">
            <em :class="'mdi mdi-' + deviceFormHeading.icon" class="mr-2"></em>
            <span>{{ deviceFormHeading.name }}</span>
          </div>
        </div>

        <button
          type="button"
          class="absolute right-[-5px] top-[-6px] z-10 ml-auto inline-flex items-center rounded-lg bg-transparent px-2 py-1.5 text-xl text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-dark-4 dark:hover:text-white"
          data-modal-hide="popup-modal"
          data-testId="closeDialogBtn"
          @click="openCancelFormPrompt"
        >
          <em class="mdi mdi-close" />
        </button>
      </header>
      <div ref="scrollContainerRef" class="grow overflow-auto pr-2" v-bind="postHogCaptureAttributes">
        <component
          :is="formComponent"
          ref="formComponentRef"
          :node="getEntity"
          :parent-id="getParentId"
          :payload="getPayload"
          @update-entity="updateEntity"
          @update-entities="updateEntities"
        />
      </div>
      <footer
        id="modal-footer"
        class="flex shrink-0 items-end justify-end gap-4 pt-2 dark:border-dark-2 keyboard-open:hidden"
      >
        <template v-if="isDialogMinimal">
          <TertiaryButton
            label="Abbrechen"
            data-testid="button-cancel"
            @click="openCancelFormPrompt"
          />
          <PrimaryButton
            :label="saveButtonLabel"
            :is-muted="!isFormValid"
            data-testid="button-save"
            class="mr-0"
            @click="openPrompt"
          />
          <BasePrompt
            :open="isPhotoPromptOpen"
            :close="closePhotoPrompt"
            :cancel="closePhotoPrompt"
            :proceed="closePhotoPromptAndSaveForm"
            :title="i18n.photoPrompt.title"
            :question="i18n.photoPrompt.question.installationLocation"
            :cancel-text="i18n.photoPrompt.cancelText"
            :proceed-text="i18n.photoPrompt.proceedText"
          />
        </template>
      </footer>
      <BasePrompt
        :title="i18n.cancelPrompt.title"
        :question="i18n.cancelPrompt.question"
        :proceed="closeFormDialog"
        :cancel="closeFormPrompt"
        :close="closeFormPrompt"
        :open="isCancelFormPromptOpen"
        :proceed-text="i18n.cancelPrompt.proceedText"
        :cancel-text="i18n.cancelPrompt.cancelText"
        data-testId="cancelFormPrompt"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useLayoutStore } from '@/store/layout/layoutStore';
import { computed, inject, onMounted, Ref, ref } from 'vue';
import { useFormStore, FormAction } from '@/store/form/formStore';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import type { Entity } from '@/models/types/Entity';
import { generateUniqueId, Notification } from '@/models';
import TertiaryButton from '@/components/UI/Button/TertiaryButton.vue';
import PrimaryButton from '@/components/UI/Button/PrimaryButton.vue';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';
import { entityTypes } from '@/enums/generic';
import {
  deviceTypeList,
  deviceTypes,
  legacyDeviceTypeList,
  radiatorDeviceTypeList,
} from '@/enums/device';
import { ANALYTICS_PROVIDER } from '@/plugins/bxAnalytics';
import { useOrderStore } from '@/store/order/orderStore';
import BasePrompt from '@/components/UI/Modal/BasePrompt.vue';
import { useInspectedBuildingStore } from '@/store/entities/inspectedBuildingStore';
import i18n from '@/utils/i18n/form.json';

import BuildingForm from '@/components/Forms/BuildingForm.vue';
import RoomForm from '@/components/Forms/RoomForm.vue';
import RoomgroupForm from '@/components/Forms/RoomgroupForm.vue';
import DeviceForm from '@/components/Forms/DeviceForm.vue';
import PlantDataForm from '@/components/Forms/PlantDataForm.vue';
import RadiatorForm from '@/components/Forms/RadiatorForm.vue';
import FunkcheckForm from '@/components/Forms/FunkcheckForm.vue';
import InspectedBuildingForm from '@/components/Forms/InspectedBuildingForm.vue';
import InspectionForm from '@/components/Forms/InspectionForm.vue';
import { Device, SmartRadiatorThermostat } from '@/models/devices/Device';
import { InspectionDevice } from '@/models/inspectionPoint/InspectionDevice';
import TKDDeviceForm from '@/components/Forms/TKDDeviceForm.vue';
import ExchangeDeviceForm from '@/components/Forms/ExchangeDeviceForm.vue';
import StaircaseTestForm from '@/components/Forms/StaircaseTestForm.vue';

type UpdateEntityPayload = {
  suppressEntityCrudEvent?: boolean;
};

type UpdateEntityItem =
  | Entity
  | {
      entity: Entity;
      payload?: UpdateEntityPayload;
    };

const formStore = useFormStore();
const entitiesStore = useEntitiesStore();
const orderStore = useOrderStore();

/*
 * Import paths have to be known at build time
 * for Vite to be able to resolve them
 * and for HMR to work
 */
const formImports: any = {
  building: BuildingForm,
  room: RoomForm,
  roomgroup: RoomgroupForm,
  installationPoint: DeviceForm,
  plant: PlantDataForm,
  radiator: RadiatorForm,
  Funkcheck: FunkcheckForm,
  inspectionPoint: InspectionForm,
  inspectedBuilding: InspectedBuildingForm,
  staircaseTest: StaircaseTestForm,
};

const customFormImports: any = {
  TKDService: TKDDeviceForm,
  SRTExchange: ExchangeDeviceForm,
  StaircaseTest: StaircaseTestForm,
};

const formComponent = computed(() => {
  const formName = formStore.getForm.name as string;

  if (formStore.isTKDService) {
    return customFormImports['TKDService'];
  }

  if (
    formStore.isReplaceFormAction() &&
    formStore.editNode instanceof SmartRadiatorThermostat
  )
    return customFormImports['SRTExchange'];

  return formImports[formName];
});

onMounted(() => {
  formStore.resetValidation();
  if (getPayload.value?.scrollToBottom && scrollContainerRef.value) {
    scrollContainerRef.value.scrollTop = scrollContainerRef.value.scrollHeight;
  }
});

const currentFormId = computed(() => {
  return formStore.getForm?.id as string;
});

const postHogCaptureAttributes = computed(() => {
  const entity = getEntity.value;
  const activeOrder = orderStore.activeOrder;

  return {
    'data-ph-capture-attribute-form_id': formId,
    'data-ph-capture-attribute-entity_id': entity?.id,
    'data-ph-capture-attribute-entity_type': entity?.type,
    'data-ph-capture-attribute-order_id': activeOrder?.id,
  };
});

const analytics = inject(ANALYTICS_PROVIDER);
const formId = generateUniqueId();
analytics?.captureEvent('open_form', {
  formId,
  entityId: currentFormId.value,
});

const layoutStore = useLayoutStore();

const isCancelFormPromptOpen = computed(
  () => layoutStore.isCancelFormPromptOpen
);
const isPhotoPromptOpen = computed(() => layoutStore.isPhotoPromptOpen);

const closeFormPrompt = () => layoutStore.setCancelPromptState(false);

const closePhotoPrompt = () => layoutStore.setPhotoPromptState(false);

const closeFormDialog = () => {
  layoutStore.closeFormDialog();
};

const closePhotoPromptAndSaveForm = () => {
  closePhotoPrompt();
  onSave();
};

const openPrompt = () => {
  if (!isInstallationLocationTest.value || hasImage.value) {
    onSave();
    return;
  }
  if (!isFormValid.value) {
    validateForm();
    return;
  }

  layoutStore.setPhotoPromptState(true);
};

const openCancelFormPrompt = () => {
  if (!formStore.hasFormChanges) {
    closeFormDialog();
    return;
  }
  layoutStore.setCancelPromptState(true);
};

const hasImage = computed(() => {
  return formComponentRef.value?.hasImage;
});

const isRadiatorForm = computed(() => {
  return (
    (formStore.getForm.name as keyof typeof formImports) ===
      entityTypes.radiator || formStore.editNode?.type === entityTypes.radiator
  );
});

const isOnSiteInspection = computed(() => {
  return orderStore.isOnSiteInspection();
});

const isInstallationLocationTest = computed(() => {
  return (
    isOnSiteInspection.value &&
    deviceFormHeading.value?.value === deviceTypes.LoRaWanGateway
  );
});

const isDialogMinimal = computed(() => {
  return !(
    (deviceFormHeading.value?.value === deviceTypes.HeatCostAllocator &&
      formStore.formAction !== FormAction.UNMOUNT) ||
    formStore.editNode?.type === entityTypes.radiator ||
    formStore.currentForm?.name === 'Funkcheck' ||
    formStore.isTKDService
  );
});

const getEntity = computed(() => {
  if (formStore.currentForm?.payload?.node) {
    const installationPoint = new InstallationPoint(
      formStore.currentForm.payload.node.roomId
    );
    installationPoint.updateProperties(formStore.currentForm.payload.node);
    return installationPoint;
  }

  if (
    formStore.currentForm?.payload?.radiatorId &&
    !formStore.currentForm?.payload?.new
  ) {
    return useInstallationPointStore().getInstallationPointsByRadiatorId(
      formStore.currentForm.payload.radiatorId
    )[0];
  }

  if (formStore.currentForm.name === 'inspectedBuilding') {
    return useInspectedBuildingStore().getCurrentInspectedBuilding();
  }

  if (!currentFormId.value) {
    return undefined;
  }

  return entitiesStore.getEntityById(currentFormId.value);
});

const getParentId = computed(() => {
  if (formStore.currentForm?.payload?.node) {
    return formStore.currentForm.payload.node.roomId;
  }
  if (formStore.getForm.parentId) {
    return formStore.getForm.parentId;
  }
  return undefined;
});

const getPayload = computed(() => {
  if (formStore.getForm.payload) {
    return formStore.getForm.payload;
  }
  return undefined;
});

const saveButtonLabel = computed(() => {
  if (formStore.getForm.name === 'inspectedBuilding') {
    return 'Speichern';
  }
  return currentFormId.value ? 'Speichern' : 'Anlegen';
});

const entityTypeTranslation: { [key: string]: string } = {
  building: 'Gebäude',
  inspectedBuilding: 'Begehungsgebäude',
  room: 'Raum',
  roomgroup: 'Nutzeinheit',
  device: 'Gerät',
  inspectionDevice: 'Testgerät',
  installationPoint: 'Gerät',
  inspectionPoint: 'Testgerät',
  plant: 'Anlage',
  radiator: 'Heizkörper',
  Funkcheck: 'Funkcheck',
  staircaseTest: 'Messung vor der Tür',
};

const formHeading = computed(() => {
  const entityType = formStore.getForm.name as string;
  let editType;

  editType = getEntity.value?.id ? 'bearbeiten' : 'anlegen';

  if (formStore.formAction === FormAction.UNMOUNT) {
    editType = 'demontieren';
  }
  if (isOnSiteInspection.value) {
    if (
      deviceFormHeading.value?.value === deviceTypes.SmartRadiatorThermostat
    ) {
      return `SRT & Adapter erfassen`;
    }
    if (deviceFormHeading.value?.value === deviceTypes.LoRaWanGateway) {
      return 'Installationsort testen';
    }
    if (entityType === 'inspectedBuilding') {
      return 'Materialplanung';
    }
    if (entityType === 'staircaseTest') {
      return 'Messung vor der Tür';
    }
  }

  return entityType === 'installationPoint'
    ? `Gerät ${editType}`
    : `${entityTypeTranslation[entityType]} ${editType}`;
});

const deviceFormHeading = computed(() => {
  const editNode = formStore.editNode;
  if (
    editNode &&
    (editNode.type === entityTypes.device ||
      editNode.type === entityTypes.inspectionDevice)
  ) {
    const allDevices = [
      ...deviceTypeList,
      ...legacyDeviceTypeList,
      ...radiatorDeviceTypeList,
    ];

    return allDevices.find((device) => device.value === editNode.deviceType);
  }
  return undefined;
});

const saveFormAnalytics = (entity: Entity) => {
  let properties: Record<any, any> = {
    formId,
    entityId: entity.id,
    entityType: entity.type,
    hasFormChanges: formStore.hasFormChanges,
  };

  if (entity instanceof Device || entity instanceof InspectionDevice) {
    properties = {
      ...properties,
      deviceType: entity.deviceType,
      serialNumber: entity.serialNumber,
    };
  }

  analytics?.captureEvent('save_form', properties);
};

const isUpdateEntityWrapper = (
  item: UpdateEntityItem
): item is { entity: Entity; payload?: UpdateEntityPayload } => {
  return typeof item === 'object' && item !== null && 'entity' in item;
};

const normalizeUpdateEntityItem = (item: UpdateEntityItem) => {
  if (!isUpdateEntityWrapper(item)) {
    return { entity: item, payload: undefined };
  }

  return item;
};

const updateEntity = (item: UpdateEntityItem) => {
  const { entity, payload } = normalizeUpdateEntityItem(item);

  let notification: Notification = new Notification();

  if (!entitiesStore.hasEntityById(entity.id)) {
    notification = generateNotificationForCreate(entity);
  }

  entitiesStore.saveEntity(entity, notification, payload);

  saveFormAnalytics(entity);
  closeFormDialog();
};

const updateEntities = (entityList: Array<UpdateEntityItem>) => {
  const [firstEntity, ...remainingEntities] = entityList;
  if (!firstEntity) {
    return;
  }

  updateEntity(firstEntity);
  remainingEntities.forEach((item) => {
    const { entity, payload } = normalizeUpdateEntityItem(item);
    entitiesStore.saveEntity(entity, undefined, payload);
  });
  closeFormDialog();
};

const generateNotificationForCreate = (
  entity: any
): Notification | undefined => {
  const entityType = entity.type;
  const notificationTranslation = entityTypeTranslation[entityType];
  if (!notificationTranslation) {
    throw new Error('Entity type does not exist');
  }

  if (entity.type === entityTypes.inspectedBuilding) return undefined;

  const notificationText = `${notificationTranslation} wurde hinzugefügt`;
  const notificationType = 'success';

  return new Notification()
    .setType(notificationType)
    .setText(notificationText)
    .setTitle(notificationText);
};

/*
 * Child Form Components should have the appropriate methods
 * to work properly
 * isFormValid -> for validation
 * handleSubmit -> for entity save
 */
type FormComponentRef = {
  isFormValid: boolean;
  handleSubmit: () => void;
  saveRadiators: () => void;
  hasImage: boolean;
};

const formComponentRef: Ref<FormComponentRef | null> = ref(null);
const scrollContainerRef = ref<HTMLElement | null>(null);

const isFormValid = computed(() => {
  return formComponentRef.value?.isFormValid;
});

const validateForm = () => {
  formStore.triggerValidation();
};

const onSave = () => {
  formStore.getForm?.payload?.onSave?.();

  if (!formComponentRef.value?.handleSubmit) {
    throw new Error(
      'Form Component must contain handleSubmit function to work properly!'
    );
  }
  if (!isFormValid.value) {
    validateForm();
    return;
  }
  if (isFormValid.value) {
    formComponentRef.value?.handleSubmit();
  }
};
</script>
